/**
 * Delete Role Use Case
 *
 * Handles role deletion with business logic validation.
 * Provides extension points for:
 * - Prevent deletion of system roles
 * - Check if role is assigned to users
 * - Reassignment workflow
 * - Auditing
 * - Event publishing (role.deleted)
 *
 * @module domains/personnel/application/use-cases/DeleteRoleUseCase
 */

import type { IRoleApiPort } from '@personnel/application/ports/IRoleApiPort';

/**
 * Creates a use case to delete a role
 *
 * @param props - Dependencies
 * @param props.apiPort - Role API port implementation
 * @returns Use case with execute method
 *
 * @example
 * const apiPort = createRoleHttpClient({ httpClient: connectApi });
 * const useCase = deleteRoleUseCase({ apiPort });
 * await useCase.execute('role-uuid-123');
 */
export const deleteRoleUseCase = (props: { apiPort: IRoleApiPort }) => {
  const { apiPort } = props;

  /**
   * Executes role deletion
   *
   * @param uuid - Role UUID to delete
   * @returns Promise resolving when deletion is complete
   * @throws Error if role cannot be deleted (system role, has users, etc.)
   *
   * @example
   * await execute('abc-123');
   */
  const execute = async (uuid: string): Promise<void> => {
    // Future business logic could go here:
    // - Validate user has permission to delete roles
    // - Check if role is system role (Admin, etc.)
    // - Check if role has assigned users
    // - Require reassignment before deletion
    // - Log audit trail
    // - Publish domain events

    await apiPort.deleteRole(uuid);
  };

  return {
    execute,
  };
};
